<template>
  <div class="bloc-sites">
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :iconHeader="hasPermission('clients.sites.add') ? 'add' : null"
      :items="sites"
      :title="$t('sites.pageName')"
      :isSelectable="hasPermission('clients.sites.view')"
      :pagination="pagination"
      :headers="tableHeaders"
      :isCustomIcon="true"
      hideDefaultFooter
      :isInfinitScroll="true"
      :itemActions="actions"
      :sortableColumns="['name', 'deliveryaccountnumber']"
      :centerColumns="['action']"
      :notSortableColumns="['action']"
      :dataCy="dataCy.sites.list"
      :isSearchable="false"
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
      @send-item="onClickRedirect"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('sites.placeholder.searchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
        <v-switch
          v-model="queryParams.includeInactive"
          :label="$t('clients.labels.switchActiveInactive')"
          @change="fetchQuery()"
        />
      </template>
      <template v-slot:item.name="{item}">
        <div :class="!item.active ? 'inactiveLeft' : ''">
          {{ item.name }}
        </div>
      </template>
      <template v-slot:item.deliveryAccountNumber="{item}">
        <div :class="!item.active ? 'inactive' : ''">
          {{ item.deliveryAccountNumber }}
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import SiteForm from '@/components/forms/SiteForm.vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import TransferSiteClientsForm from '@/views/sites/TransferSiteClientsForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import DeleteForm from '@/components/shared/widgets/DeleteForm.vue';
import { sites } from '@/aq-data-cy.json';
import perpage from '@/constants/pagination';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';

export default {
    components: {
        TableRemote,
        SearchWithFilters,
    },
    data() {
        return {
            sites: [],
            loading: false,
            initializing: false,
            pagination: {},
            queryParams: { perpage, includeInactive: false, search: '' },
            includeInactive: false,
            filterName: 'Sites',

        };
    },
    computed: {
        ...mapGetters([
            'client',
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                'name',
                'deliveryAccountNumber',
            ].map(header => ({
                text: this.$t(`sites.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];
            if (this.hasPermission('sites.transfer')) {
                actions.push({
                    name: this.$t('forms.btn.transferSite'),
                    icon: 'open_in_new',
                    mode: 'open_in_new',
                });
            }
            actions.push({
                name: this.$t('sites.active'),
                icon: 'edit',
                mode: 'edit',
                customValidationProperty: 'active',
                showOncustomValidationProperty: false,
            });
            actions.push({
                name: this.$t('sites.desactive'),
                icon: 'edit',
                mode: 'edit',
                customValidationProperty: 'active',
                showOncustomValidationProperty: true,
            });
            if (this.hasPermission('sites.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
        dataCy() {
            return {
                sites,
            };
        },
    },
    async created() {
        this.loading = true;
        await this.getSites();
        this.loading = false;
    },
    methods: {
        ...mapActions({
            reloadClient: 'getClientInfo',
        }),
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, search: queryParams?.search || '', page: 1 };
            this.pagination = {};
            this.sites = [];
            await this.getSites();
        },
        async getSites() {
            if (!this.initializing) {
                this.initializing = true;
                const id = this.$route.params.clientId;
                const { data } = await this.$rq.getClientSites(id, this.queryParams);
                this.sites = [...this.sites, ...data.sites];
                this.pagination = data.pagination;
                this.initializing = false;
            }
        },
        onClickRedirect(site) {
            this.$router.push({
                name: 'sitePage',
                params: {
                    siteId: site.id,
                },
            });
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                width: 750,
                title: this.$t('sites.addSite'),
                component: Vue.component('addSiteForm', SiteForm),
                onConfirm: requestPayload => this.addSite(requestPayload),
            });
        },
        onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            } else if (action.mode === 'open_in_new') {
                this.$root.$emit('showModal', {
                    title: this.$t('sites.transfer.title', { siteName: item.name }),
                    component: Vue.component('TransferSiteClientsForm', TransferSiteClientsForm),
                    onConfirm: requestPayload => this.transferSite(requestPayload, item),
                });
            } else if (action.mode === 'edit') {
                this.$root.$emit('showModal', {
                    title: this.$t('sitePage.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: item.active ? this.$t('clientPage.removeSiteTreeNode') : this.$t('clientPage.restoreSiteTreeNode') },
                    onConfirm: () => this.updateActive(item),
                    isForm: false,
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('sites.removeSite').toUpperCase(),
                    component: Vue.component('DeleteForm', DeleteForm),
                    buttonTitle: 'delete',
                    payload: { msg: this.$t('sites.msg.remove'), security: this.$t('sites.msg.security') },
                    isForm: true,
                    onConfirm: async () => {
                        await this.removeSite(item);
                    },
                });
            }
        },
        async removeSite(item) {
            await this.$rq.removeSite(item.id);
            this.$toasted.global.toast_success({
                message: this.$t('sites.msg.siteDeleted', { data: item.deliveryAccountNumber }),
            });
            await this.fetchQuery({});
        },
        async transferSite(requestPayload, item) {
            const { clientId } = requestPayload;
            await this.$rq.transferSiteToAnotherClient(item.id, clientId);
            this.$toasted.global.toast_success({
                message: this.$t('sites.transfer.msg.trasferred', { siteName: item.siteName }),
            });
            await this.fetchQuery({});
        },
        async addSite(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            const { id } = this.client;
            const response = await this.$rq.addSite(id, payload);
            await this.reloadClient(id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.siteCreated', { site: response.data.name }),
            });
            await this.fetchQuery({});
        },
        async updateActive(item) {
            await this.$rq.updateSiteStatus(item.id, { status: !item.active });
            await this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getSites();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.sites = [];
            this.getSites();
        },
    },
};
</script>

<style lang="scss">
    .inactive {
        color: grey
    }
    .inactiveLeft {
        color: grey;
    }
    .bloc-sites tbody :first-child .text-start:first-child:has(.inactiveLeft) {
        border-left: 8px solid grey !important;
    }
</style>
